/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { deviceIntegrationsUpsertConfig } from "../funcs/deviceIntegrationsUpsertConfig.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DeviceIntegrationConfig } from "../models/components/deviceintegrationconfig.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpsertDeviceIntegrationConfigRequest,
  UpsertDeviceIntegrationConfigSecurity,
} from "../models/operations/upsertdeviceintegrationconfig.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpsertDeviceIntegrationConfigMutationVariables = {
  request: UpsertDeviceIntegrationConfigRequest;
  security?: UpsertDeviceIntegrationConfigSecurity | undefined;
  options?: RequestOptions;
};

export type UpsertDeviceIntegrationConfigMutationData = DeviceIntegrationConfig;

export type UpsertDeviceIntegrationConfigMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * upsertConfig deviceIntegrations
 *
 * @remarks
 * Create or update the org-wide device integration config for a provider. Omit credentials to keep the stored secrets; supplying credentials rotates them in place and resets the schedules' sync state. Settings merge per key: an omitted key keeps its stored value, a supplied key overwrites it.
 */
export function useUpsertDeviceIntegrationConfigMutation(
  options?: MutationHookOptions<
    UpsertDeviceIntegrationConfigMutationData,
    UpsertDeviceIntegrationConfigMutationError,
    UpsertDeviceIntegrationConfigMutationVariables
  >,
): UseMutationResult<
  UpsertDeviceIntegrationConfigMutationData,
  UpsertDeviceIntegrationConfigMutationError,
  UpsertDeviceIntegrationConfigMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpsertDeviceIntegrationConfigMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpsertDeviceIntegrationConfig(): MutationKey {
  return ["@gram/client", "deviceIntegrations", "upsertConfig"];
}

export function buildUpsertDeviceIntegrationConfigMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpsertDeviceIntegrationConfigMutationVariables,
  ) => Promise<UpsertDeviceIntegrationConfigMutationData>;
} {
  return {
    mutationKey: mutationKeyUpsertDeviceIntegrationConfig(),
    mutationFn: function upsertDeviceIntegrationConfigMutationFn({
      request,
      security,
      options,
    }): Promise<UpsertDeviceIntegrationConfigMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(deviceIntegrationsUpsertConfig(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
